import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { GitBranch, GitFork, ArrowUpRight } from "lucide-react";
import type { StrainGraph, Strain, StrainVersion } from "@/types";
import { useStore } from "@/store/useStore";
import { FORK_INTENTS } from "@/lib/vocabulary";
import { Badge } from "@/components/Badge";
import { Avatar } from "@/components/AppShell";
import { timeAgo } from "@/lib/vocabulary";
import { cn } from "@/lib/cn";

export function LineageView({ graph }: { graph: StrainGraph }) {
  const allGraphs = useStore((s) => s.graphs);
  const rootGraph = graph.version.forked_from_strain_id
    ? allGraphs.find((g) => g.strain.id === graph.version.forked_from_strain_id) ?? graph
    : graph;
  const children = allGraphs.filter((g) => g.version.forked_from_strain_id === rootGraph.strain.id);

  return (
    <div className="h-full overflow-y-auto bg-grid-fine">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-1">lineage view</div>
          <h2 className="text-lg font-semibold">How this strain evolved</h2>
          <p className="text-[13px] text-[var(--color-ink-muted)] mt-1">
            The original and every fork that branched from it. Click a version to open it.
          </p>
        </div>

        <div className="relative pl-6">
          {/* trunk line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gradient-to-b from-[var(--color-primary)] via-[var(--color-border-bright)] to-transparent" />

          <VersionNode version={rootGraph.version} strain={rootGraph.strain} isRoot />

          {children.map((child, i) => (
            <div key={child.version.id}>
              <motion.div
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                className="relative mt-3 ml-6 pl-6"
              >
                {/* branch connector */}
                <div className="absolute left-[-1px] top-0 w-6 h-8 border-l border-t border-[var(--color-border-bright)] rounded-tl-lg" />
                <VersionNode version={child.version} strain={child.strain} />
              </motion.div>
            </div>
          ))}

          {children.length === 0 && (
            <div className="ml-6 mt-4 text-[13px] text-[var(--color-ink-faint)] italic">
              No forks yet. Fork this strain to start a new branch.
            </div>
          )}
        </div>

        {/* fork intents legend */}
        <div className="mt-8 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-2">fork intents</div>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(FORK_INTENTS).map(([k, m]) => (
              <div key={k} className="flex items-center gap-2 text-[12px]">
                <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                <span className="text-[var(--color-ink-soft)]">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function VersionNode({ version, strain, isRoot }: { version: StrainVersion; strain: Strain; isRoot?: boolean }) {
  const intentMeta = version.fork_intent ? FORK_INTENTS[version.fork_intent as keyof typeof FORK_INTENTS] : null;
  return (
    <Link
      to={`/s/${strain.slug}`}
      className={cn(
        "block rounded-2xl border bg-[var(--color-surface)] p-4 hover:border-[var(--color-primary)] transition-colors group",
        isRoot ? "border-[var(--color-primary)] shadow-glow-primary" : "border-[var(--color-border)]",
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        {isRoot ? (
          <Badge tone="primary" icon={<GitBranch size={10} />}>original · v{version.version_number}</Badge>
        ) : (
          <Badge tone="inferred" icon={<GitFork size={10} />}>fork · v{version.version_number}</Badge>
        )}
        {intentMeta && <Badge tone="muted">{intentMeta.label}</Badge>}
      </div>
      <div className="text-[14px] font-semibold text-[var(--color-ink)] group-hover:text-[var(--color-primary)] transition-colors flex items-start gap-1.5">
        {strain.title}
        <ArrowUpRight size={14} className="text-[var(--color-ink-faint)] group-hover:text-[var(--color-primary)] mt-0.5 shrink-0" />
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Avatar initials={strain.creator_avatar ?? strain.creator.slice(0, 2).toUpperCase()} size={18} />
        <span className="text-[11px] text-[var(--color-ink-muted)]">{strain.creator}</span>
        <span className="text-[11px] text-[var(--color-ink-faint)]">· {timeAgo(version.created_at)}</span>
      </div>
      {version.change_summary && (
        <div className="mt-2 text-[12px] text-[var(--color-ink-muted)] italic">{version.change_summary}</div>
      )}
    </Link>
  );
}
